import { NON_WEBSOCKET_PAGES } from '~/constants/socket'
import { bridge } from '~/services/bridge'
import { getPluginAccessToken } from '~/services/bridge/pluginAuth'
import { deferred, Deferred } from '~/utils/async'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { trace } from './analyticsService'

// Re-export for convenience
export { SocketEvent } from '~/constants/socket'

export enum SocketAction {
  OrganizationIndexes = 'organization.indexes'
}

type SocketType = 'workspace' | 'user'

/** Base interface for WebSocket event data */
export interface SocketEventData {
  event: string
  request_id?: string
  data?: unknown
  error?: unknown
  _socketType?: SocketType
}

/** Handler type for WebSocket events */
export type SocketEventHandler = (data: SocketEventData) => void

interface SocketInstance {
  socket: WebSocket | null
  type: SocketType
  workspaceUuid?: string // Only for workspace type
  retryCount: number
  reconnectTimeout: ReturnType<typeof setTimeout> | null
}

const MAX_RETRIES = 100
const RECONNECT_DELAY_MS = 1000

const deferredValues: Map<string, Deferred<unknown>> = new Map()

/**
 * WebSocket architecture:
 *
 * - UserSocket (/api/ws/) is the PRIMARY connection. It's registered under
 *   both user:{id} and all org namespaces on the server, so it receives
 *   account-level events (billing, license, agent status) AND workspace-level
 *   events (tasks, conversations, indexes) for every org the user belongs to.
 *
 * - WorkspaceSocket (/api/workspace/{uuid}/ws) is DEPRECATED for frontend use.
 *   It remains available for IDE plugins that connect with workspace context.
 *   The frontend should use only UserSocket.
 */

// Workspace-scoped WebSocket instance (deprecated for frontend, kept for plugins)
const workspaceInstance: SocketInstance = {
  socket: null,
  type: 'workspace',
  workspaceUuid: undefined,
  retryCount: 0,
  reconnectTimeout: null
}

// User-level WebSocket instance (primary connection)
const userInstance: SocketInstance = {
  socket: null,
  type: 'user',
  retryCount: 0,
  reconnectTimeout: null
}

function getWsBaseUrl(): { protocol: string; host: string } {
  if (bridge.isInPlugin()) {
    const baseUrl = bridge.getBaseUrl()
    const url = new URL(baseUrl)
    const protocol = url.protocol === 'https:' ? 'wss' : 'ws'
    return { protocol, host: url.host }
  }
  const protocol = location.protocol === 'https:' ? 'wss' : 'ws'
  return { protocol, host: location.host }
}

function appendPluginToken(params: URLSearchParams): void {
  if (bridge.isInPlugin()) {
    const token = getPluginAccessToken()
    if (token) params.set('token', token)
  }
}

function buildWorkspaceUrl(workspaceUuid: string): string {
  const { protocol, host } = getWsBaseUrl()
  const wsPath = `/api/workspace/${workspaceUuid}/ws`
  const queryParams = new URLSearchParams()
  appendPluginToken(queryParams)
  const qs = queryParams.toString()
  return `${protocol}://${host}${wsPath}${qs ? `?${qs}` : ''}`
}

function buildUserUrl(): string {
  const { protocol, host } = getWsBaseUrl()
  const wsPath = `/api/ws/`
  const queryParams = new URLSearchParams()
  appendPluginToken(queryParams)
  const qs = queryParams.toString()
  return `${protocol}://${host}${wsPath}${qs ? `?${qs}` : ''}`
}

function createSocket(instance: SocketInstance, url: string): void {
  if (instance.reconnectTimeout) {
    clearTimeout(instance.reconnectTimeout)
    instance.reconnectTimeout = null
  }

  instance.socket = new WebSocket(url)

  instance.socket.addEventListener('open', () => {
    instance.retryCount = 0
    trace({
      event: 'ws_open',
      labels: {
        type: instance.type
      }
    })
    console.log(`[Socket] ${instance.type} WebSocket connected`)
  })

  instance.socket.addEventListener('message', (e: MessageEvent) => {
    handleMessage(instance, e)
  })

  instance.socket.addEventListener('close', (event: CloseEvent) => {
    handleClose(instance, event)
  })

  instance.socket.addEventListener('error', (e: Event) => {
    trace({
      event: 'ws_error',
      labels: {
        type: instance.type,
        error: e.toString()
      }
    })
  })
}

function handleMessage(instance: SocketInstance, e: MessageEvent): void {
  const data = JSON.parse(e.data)
  const { request_id, data: payload, error } = data
  const deferredValue = deferredValues.get(request_id)

  if (deferredValue) {
    if (error) {
      return deferredValue.reject(error)
    }
    return deferredValue.resolve(payload)
  } else {
    globalEventEmitter.emit(AppEvents.SocketEvent, { ...data, _socketType: instance.type })
  }
}

async function handleClose(instance: SocketInstance, event: CloseEvent): Promise<void> {
  const { code, reason } = event

  trace({
    event: 'ws_close',
    labels: {
      type: instance.type,
      code: String(code),
      reason: reason
    }
  })

  console.log(`[Socket] ${instance.type} WebSocket closed. code=${code}, reason=${reason}`)

  if (code === 1000) {
    console.log(`[Socket] ${instance.type} WebSocket closed normally, not reconnecting`)
    return
  }

  if (
    instance.socket &&
    (instance.socket.readyState === WebSocket.CONNECTING ||
      instance.socket.readyState === WebSocket.OPEN)
  ) {
    instance.retryCount = 0
    return
  }

  if (instance.retryCount >= MAX_RETRIES) {
    console.error(
      `[Socket] ${instance.type} WebSocket failed to reconnect after ${MAX_RETRIES} attempts`
    )
    instance.retryCount = 0
    return
  }

  instance.retryCount++
  const delay = Math.min(RECONNECT_DELAY_MS * Math.pow(1.5, instance.retryCount - 1), 30000)
  console.log(
    `[Socket] ${instance.type} WebSocket reconnecting in ${delay}ms (attempt ${instance.retryCount}/${MAX_RETRIES})`
  )

  instance.reconnectTimeout = setTimeout(() => {
    if (instance.type === 'workspace' && instance.workspaceUuid) {
      const url = buildWorkspaceUrl(instance.workspaceUuid)
      createSocket(instance, url)
    } else if (instance.type === 'user') {
      const url = buildUserUrl()
      createSocket(instance, url)
    }
  }, delay)
}

function disconnectInstance(instance: SocketInstance): void {
  if (instance.reconnectTimeout) {
    clearTimeout(instance.reconnectTimeout)
    instance.reconnectTimeout = null
  }

  if (instance.socket) {
    if (
      instance.socket.readyState === WebSocket.OPEN ||
      instance.socket.readyState === WebSocket.CONNECTING
    ) {
      instance.socket.close(1000, 'Client disconnect')
    }
    instance.socket = null
  }

  instance.retryCount = 0
  if (instance.type === 'workspace') {
    instance.workspaceUuid = undefined
  }
}

function isInstanceConnected(instance: SocketInstance): boolean {
  return instance.socket !== null && instance.socket.readyState === WebSocket.OPEN
}

function sendOnInstance(instance: SocketInstance, message: string): void {
  if (isInstanceConnected(instance)) {
    instance.socket?.send(message)
  }
}

// ============================================================================
// WorkspaceSocket - DEPRECATED for frontend use. Kept for IDE plugin compat.
// The frontend should use UserSocket for all events.
// ============================================================================

export const WorkspaceSocket = {
  /** @deprecated Use UserSocket — the server now routes workspace events to user sockets. */
  connect(workspaceUuid: string): void {
    if (!bridge.isInPlugin()) {
      try {
        const { pathname } = new URL(location.href)
        if (NON_WEBSOCKET_PAGES.includes(pathname)) return
      } catch {
        // Ignore URL parse errors
      }
    }

    if (
      workspaceInstance.workspaceUuid === workspaceUuid &&
      isInstanceConnected(workspaceInstance)
    ) {
      return
    }

    if (workspaceInstance.socket) {
      disconnectInstance(workspaceInstance)
    }

    workspaceInstance.workspaceUuid = workspaceUuid
    const url = buildWorkspaceUrl(workspaceUuid)
    createSocket(workspaceInstance, url)
  },

  /** @deprecated */
  disconnect(): void {
    disconnectInstance(workspaceInstance)
  },

  /** @deprecated */
  reconnect(): void {
    if (workspaceInstance.workspaceUuid) {
      const uuid = workspaceInstance.workspaceUuid
      this.disconnect()
      this.connect(uuid)
    }
  },

  /** @deprecated */
  isConnected(): boolean {
    return isInstanceConnected(workspaceInstance)
  },

  /** @deprecated */
  send(message: string): void {
    sendOnInstance(workspaceInstance, message)
  },

  /** @deprecated */
  async callAction<T = unknown>(
    action: SocketAction,
    data: Record<string, unknown> | null = null
  ): Promise<T> {
    const deferredValue = deferred<T>()
    const requestId = Math.random().toString(36).substring(7)
    const payload = {
      request_id: requestId,
      action,
      data
    }
    this.send(JSON.stringify(payload))
    deferredValues.set(requestId, deferredValue as Deferred<unknown>)

    const value = await Promise.race([
      deferredValue,
      new Promise((_, reject) =>
        setTimeout(() => {
          return reject('Timeout')
        }, 15_000)
      )
    ])
    deferredValues.delete(requestId)

    return value as T
  },

  /** @deprecated */
  on(event: string, handler: SocketEventHandler): void {
    globalEventEmitter.on(AppEvents.SocketEvent, data => {
      if ((data as SocketEventData).event === event) {
        handler(data as SocketEventData)
      }
    })
  },

  /** @deprecated */
  off(event: string, handler: SocketEventHandler): void {
    globalEventEmitter.off(AppEvents.SocketEvent, handler)
  }
}

// ============================================================================
// UserSocket - PRIMARY connection for all WebSocket events.
// Receives both account-level and workspace-level events.
// ============================================================================

export const UserSocket = {
  /**
   * Connect to the user-level WebSocket endpoint.
   * This is the primary connection — the server registers it under
   * the user namespace AND all org namespaces the user belongs to.
   */
  connect(): void {
    if (isInstanceConnected(userInstance)) {
      return
    }

    if (userInstance.socket) {
      disconnectInstance(userInstance)
    }

    const url = buildUserUrl()
    createSocket(userInstance, url)
  },

  disconnect(): void {
    disconnectInstance(userInstance)
  },

  reconnect(): void {
    this.disconnect()
    this.connect()
  },

  isConnected(): boolean {
    return isInstanceConnected(userInstance)
  },

  send(message: string): void {
    sendOnInstance(userInstance, message)
  },

  async callAction<T = unknown>(
    action: SocketAction,
    data: Record<string, unknown> | null = null
  ): Promise<T> {
    const deferredValue = deferred<T>()
    const requestId = Math.random().toString(36).substring(7)
    const payload = {
      request_id: requestId,
      action,
      data
    }
    this.send(JSON.stringify(payload))
    deferredValues.set(requestId, deferredValue as Deferred<unknown>)

    const value = await Promise.race([
      deferredValue,
      new Promise((_, reject) =>
        setTimeout(() => {
          return reject('Timeout')
        }, 15_000)
      )
    ])
    deferredValues.delete(requestId)

    return value as T
  },

  on(event: string, handler: SocketEventHandler): void {
    globalEventEmitter.on(AppEvents.SocketEvent, data => {
      if ((data as SocketEventData).event === event) {
        handler(data as SocketEventData)
      }
    })
  },

  off(event: string, handler: SocketEventHandler): void {
    globalEventEmitter.off(AppEvents.SocketEvent, handler)
  }
}
